import {
  BASE_GENRE_TAGS_MAP,
  GENRE_LABELS,
  GENRE_TAG_TO_LABEL,
  GENRE_TAGS,
  REGION_IDS,
} from 'spa/components/filters/category_filter_panel'
import { connect } from 'react-redux'
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl'
import { Loading } from 'spa/components/loading'
import { selectPreference } from 'spa/action_creators/preferences'
import { Subheading } from 'spa/components/typography'
import { TextStyle } from 'spa/components/typography'
import classNames from 'classnames'
import isEqual from 'lodash/isEqual'
import PreferencesListItem from './preferences_list_item'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import styles from './styles'

const tooltips = defineMessages({
  hiphop: {
    id: 'djcity.account.preferences.tooltip.hiphop',
    defaultMessage: 'Rap, Jersey Club, Trap, +MORE',
  },
  dance: {
    id: 'djcity.account.preferences.tooltip.dance',
    defaultMessage: 'Tech house, Afro house, Nu Disco, +MORE',
  },
  latin: {
    id: 'djcity.account.preferences.tooltip.latin_genre',
    defaultMessage: 'Urbano, Tropical, Reggaeton, +MORE',
  },
  pop: {
    id: 'djcity.account.preferences.tooltip.pop',
    defaultMessage: 'K-Pop, Viral, Indie, +MORE',
  },
  rnb: {
    id: 'djcity.account.preferences.tooltip.rnb',
    defaultMessage: 'Soul, Slow Jam, Uptempo R&B, +MORE',
  },
  african: {
    id: 'djcity.account.preferences.tooltip.african',
    defaultMessage: 'Afrobeats, Afro House, Amapiano, +MORE',
  },
  other: {
    id: 'djcity.account.preferences.tooltip.other',
    defaultMessage: 'Reggae, Dancehall, Baile Funk, +MORE',
  },
  2: {
    id: 'djcity.account.preferences.tooltip.asia',
    defaultMessage: 'Japanese Music, K-Pop, +MORE',
  },
  3: {
    id: 'djcity.account.preferences.tooltip.uk',
    defaultMessage: 'UK Music, Drum & Bass, Drill, +MORE',
  },
  4: {
    id: 'djcity.account.preferences.tooltip.germany',
    defaultMessage: 'German & French Music, Techno, +MORE',
  },
  6: {
    id: 'djcity.account.preferences.tooltip.latin_region',
    defaultMessage: 'Regional Mexicano, Urbano, Dembow, +MORE',
  },
  7: {
    id: 'djcity.account.preferences.tooltip.south_asia',
    defaultMessage: 'Bollywood, Bhangra, Punjabi, +MORE',
  },
})

const regionIdRenderingOrder = {
  [REGION_IDS.USA]: null, // do not render
  [REGION_IDS.ASIA]: 2,
  [REGION_IDS.UK]: 5,
  [REGION_IDS.GERMANY]: 4,
  [REGION_IDS.REGGAE_NY]: null, // do not render
  [REGION_IDS.LATIN]: 1,
  [REGION_IDS.SOUTH_ASIA]: 3,
}

const PreferencesList = ({
  forceClosePopover,
  preferences = [],
  genrePreferences = {},
  isFromPopup,
  isLoading,
  onPreferenceSelected,
  queryParams,
  setQueryParams,
  intl,
}) => {
  const [myPreferences, setMyPreferences] = useState(
    structuredClone(preferences)
  )
  const [myGenrePreferences, setMyGenrePreferences] = useState(
    structuredClone(genrePreferences)
  )
  const [myPreferencesClicked, setMyPreferencesClicked] = useState(false)
  const [myGenrePreferencesClicked, setMyGenrePreferencesClicked] = useState(
    false
  )

  const preferencesFiltered = preferences.some(
    region =>
      ![REGION_IDS.USA, REGION_IDS.REGGAE_NY].includes(region.regionId) &&
      region.selected &&
      !region.autoselected
  )
  const genrePreferencesFiltered = Object.values(genrePreferences).some(
    value => value
  )
  const isFiltered = preferencesFiltered || genrePreferencesFiltered

  useEffect(() => {
    setMyPreferences(structuredClone(preferences))
  }, [preferences])

  useEffect(() => {
    setMyGenrePreferences(structuredClone(genrePreferences))
  }, [genrePreferences])

  if (isLoading) {
    return <Loading />
  }

  const handlePreferenceClick = regionId => {
    const selected = myPreferences.map(pref => {
      if (pref.regionId === regionId) {
        if (pref.autoselected) {
          pref.autoselected = false
          pref.selected = false
          myPreferences.forEach(p => {
            if (p.regionId !== regionId && p.autoselected) {
              p.autoselected = false
              p.selected = true
            }
          })
        } else {
          if (regionId === REGION_IDS.LATIN && !pref.selected) {
            if (myGenrePreferences.hide_latin) {
              handleGenrePreferenceClick('hide_latin')
            }
          }
          pref.selected = !pref.selected
        }
      }
      return pref
    })

    setMyPreferences(selected)

    if (isEqual(selected, preferences)) {
      setMyPreferencesClicked(false)
    } else {
      setMyPreferencesClicked(true)
    }
  }

  const handleGenrePreferenceClick = hide_genre => {
    if (
      // make sure at least one genre is selected, otherwise don't allow unselecting the last genre
      Object.entries(myGenrePreferences).some(
        ([key, value]) => key !== hide_genre && !value
      )
    ) {
      if (hide_genre === 'hide_latin' && !myGenrePreferences[hide_genre]) {
        if (
          myPreferences.find(pref => pref.regionId === REGION_IDS.LATIN)
            ?.selected
        ) {
          handlePreferenceClick(REGION_IDS.LATIN)
        }
      }

      const selected = {
        ...myGenrePreferences,
        [hide_genre]: !myGenrePreferences[hide_genre],
      }

      setMyGenrePreferences(selected)

      if (isEqual(selected, genrePreferences)) {
        setMyGenrePreferencesClicked(false)
      } else {
        setMyGenrePreferencesClicked(true)
      }
    }
  }

  const restorePreferences = () => {
    const defaultPreferences = myPreferences.map(pref => {
      if (!pref.required) {
        pref.selected = false
      }
      return pref
    })

    const defaultGenrePreferences = Object.keys(myGenrePreferences).reduce(
      (acc, key) => {
        acc[key] = false
        return acc
      },
      {}
    )

    setMyPreferences(defaultPreferences)
    setMyGenrePreferences(defaultGenrePreferences)

    if (!isEqual(defaultPreferences, preferences)) {
      setMyPreferencesClicked(true)
    }
    if (!isEqual(defaultGenrePreferences, genrePreferences)) {
      setMyGenrePreferencesClicked(true)
    }
  }

  const submitPreferences = () => {
    // redirect to "/new-releases" in case the current region or genre was hidden
    if (queryParams && setQueryParams) {
      if (queryParams.regionId !== 0 && myPreferences) {
        const region = myPreferences.find(
          region => region.regionId === queryParams.regionId
        )
        if (region && !region.selected && !region.autoselected) {
          setQueryParams({ ...queryParams, genres: [], regionId: 0 })
        }
      } else if (queryParams.genres.length && myGenrePreferences) {
        const genre = queryParams.genres[0]
        const hide_genre = `hide_${BASE_GENRE_TAGS_MAP[genre]?.toLowerCase()}`
        if (myGenrePreferences[hide_genre]) {
          setQueryParams({ ...queryParams, genres: [] })
        }
      }
    }

    const regionIDs = myPreferences
      .filter(pref => pref.selected)
      .map(pref => pref.regionId)

    const useCurrentLocaleAsRegionPreference = myPreferences.some(
      pref => pref.autoselected
    )

    onPreferenceSelected(
      {
        regionIDs,
        useCurrentLocaleAsRegionPreference,
      },
      myGenrePreferences,
      myPreferencesClicked,
      myGenrePreferencesClicked
    )

    setMyPreferencesClicked(false)
    setMyGenrePreferencesClicked(false)
    if (forceClosePopover) forceClosePopover()
  }

  const preferenceList = myPreferences
    .filter(preference => regionIdRenderingOrder[preference.regionId])
    .sort(
      (a, b) =>
        regionIdRenderingOrder[a.regionId] - regionIdRenderingOrder[b.regionId]
    )
    .map((preference, index) => {
      return (
        <PreferencesListItem
          autoselected={preference.autoselected}
          handleClick={() => handlePreferenceClick(preference.regionId)}
          key={index}
          label={intl.formatMessage(GENRE_LABELS[preference.regionId])}
          required={preference.required}
          selected={preference.selected}
          tooltip={intl.formatMessage(tooltips[preference.regionId])}
        />
      )
    })

  const genrePreferenceList = Object.keys(myGenrePreferences || {}).map(
    (genrePreference, index) => {
      return (
        <PreferencesListItem
          handleClick={() => handleGenrePreferenceClick(genrePreference)}
          key={index}
          label={intl.formatMessage(
            GENRE_TAG_TO_LABEL[
              GENRE_TAGS[genrePreference.replace('hide_', '').toUpperCase()]
            ]
          )}
          selected={!myGenrePreferences[genrePreference]}
          tooltip={intl.formatMessage(
            tooltips[genrePreference.replace('hide_', '')]
          )}
        />
      )
    }
  )

  return (
    <div
      className={classNames(styles.preferencesContainer, {
        [styles.preferencesContainerPopup]: isFromPopup,
      })}
    >
      <div>
        <TextStyle
          className={styles.preferencesTitle}
          uppercase
          variant="extra-bold"
        >
          <FormattedMessage
            defaultMessage="New releases settings"
            id="djcity.account.preferences.header"
          />
        </TextStyle>
        <Subheading className={styles.subHeader}>
          <FormattedMessage
            defaultMessage="Customize the music you see on the NEW RELEASES page. You can still access all music by searching or browsing the site."
            id="djcity.records.music.preference.subheader"
          />
        </Subheading>
      </div>
      <div>
        <TextStyle uppercase variant="extra-bold">
          <FormattedMessage
            defaultMessage="Main genre categories"
            id="djcity.account.preferences.category_1"
          />
        </TextStyle>
        <div className={styles.preferenceList}>{genrePreferenceList}</div>
      </div>
      <div>
        <TextStyle uppercase variant="extra-bold">
          <FormattedMessage
            defaultMessage="More global music"
            id="djcity.account.preferences.category_2"
          />
        </TextStyle>
        <div className={styles.preferenceList}>{preferenceList}</div>
      </div>
      <div>
        <button
          className={classNames('secondary', styles.restorePreferences, {
            inactive: !isFiltered,
          })}
          onClick={restorePreferences}
        >
          <FormattedMessage
            defaultMessage="Restore defaults"
            id="djcity.account.preferences.restore_defaults"
          />
        </button>
        <button
          className={classNames('primary', styles.savePreferences, {
            inactive: !(myPreferencesClicked || myGenrePreferencesClicked),
          })}
          onClick={submitPreferences}
        >
          <FormattedMessage
            defaultMessage="Save changes"
            id="djcity.account.preferences.save"
          />
        </button>
      </div>
    </div>
  )
}

PreferencesList.propTypes = {
  displayMessage: PropTypes.func,
  forceClosePopover: PropTypes.func,
  genrePreferences: PropTypes.object,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  isFromPopup: PropTypes.bool,
  isLoading: PropTypes.bool,
  onPreferenceSelected: PropTypes.func,
  preferences: PropTypes.arrayOf(
    PropTypes.shape({
      regionId: PropTypes.number,
      required: PropTypes.bool,
      autoselected: PropTypes.bool,
      selected: PropTypes.bool,
      label: PropTypes.string,
    })
  ),
  queryParams: PropTypes.object,
  setQueryParams: PropTypes.func,
}

const mapStateToProps = state => ({
  isLoading: state.preferences.isLoading,
  preferences: state.preferences.preferences,
  genrePreferences: state.preferences.genrePreferences,
})

const mapDispatchToProps = dispatch => ({
  onPreferenceSelected: (
    preferences,
    genrePreferences,
    preferencesClicked,
    genrePreferencesClicked
  ) =>
    dispatch(
      selectPreference(
        preferences,
        genrePreferences,
        preferencesClicked,
        genrePreferencesClicked
      )
    ),
})

const ConnectedPreferencesList = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(PreferencesList))

export { ConnectedPreferencesList as PreferencesList }
